import { FormEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import AuthShell from '../../components/auth/AuthShell';
import { REQUEST_PASSWORD_RESET_MUTATION } from '../../graphql/mutations';
import styles from './ForgotPassword.module.css';

const ForgotPassword = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [sentTo, setSentTo] = useState('');
  const [error, setError] = useState('');

  const [requestReset, { loading }] = useMutation(REQUEST_PASSWORD_RESET_MUTATION, {
    onCompleted: () => setSentTo(email.trim()),
    onError: (err) => setError(err.message),
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError('');
    requestReset({ variables: { email: email.trim() } });
  };

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Enter the email you log in with. We'll send you a link to choose a new password."
    >
      {sentTo ? (
        <div className="space-y-6">
          <div className={styles.sent} role="status">
            <span className="material-symbols-outlined">mark_email_read</span>
            <p>
              If an account exists for <strong>{sentTo}</strong>, a reset link is on its way. The link
              works once, for 2 hours. Nothing there? Check your spam folder, or send it again.
            </p>
          </div>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => { setSentTo(''); setError(''); }}
          >
            Send it again
          </button>
        </div>
      ) : (
        <>
          {error && (
            <div className="mb-4 p-3 bg-error-container rounded-lg flex items-center gap-2 text-sm text-on-error-container" role="alert">
              <span className="material-symbols-outlined text-base">error</span>
              {error}
            </div>
          )}
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-on-surface-variant font-label" htmlFor="reset-email">
                Work Email
              </label>
              <div className={styles.inputWrapper}>
                <span className={`material-symbols-outlined text-lg ${styles.inputIcon}`}>alternate_email</span>
                <input
                  className={styles.input}
                  id="reset-email"
                  type="email"
                  placeholder="name@company.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 btn-primary rounded-lg shadow-md shadow-primary/20 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? 'Sending…' : 'Send reset link'}
            </button>
          </form>
        </>
      )}
      <div className="mt-8 text-center">
        <Link to="/login" className={styles.backLink}>
          <span className="material-symbols-outlined text-base">arrow_back</span>
          Back to log in
        </Link>
      </div>
    </AuthShell>
  );
};

export default ForgotPassword;
